import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';

interface ConversationStats {
  open: number;
  unattended: number;
  unassigned: number;
  pending: number;
}

interface AgentStatus {
  online: number;
  busy: number;
  offline: number;
}

interface AgentConversation {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  isActive: boolean;
  open: number;
  unattended: number;
}

const ReportsView: React.FC = () => {
  const [conversationStats, setConversationStats] = useState<ConversationStats | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [trafficData, setTrafficData] = useState<any>({});
  const [agentConversations, setAgentConversations] = useState<AgentConversation[]>([]);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // axios already has /api as baseURL, so just use /analytics/...
      const [statsRes, statusRes, trafficRes, agentsRes] = await Promise.all([
        axios.get(`/analytics/conversations/stats?period=${period}`),
        axios.get(`/analytics/agents/status`),
        axios.get(`/analytics/conversations/traffic?period=${period}`),
        axios.get(`/analytics/agents/conversations`)
      ]);

      setConversationStats(statsRes.data.data);
      setAgentStatus(statusRes.data.data);
      setTrafficData(trafficRes.data.data);
      setAgentConversations(agentsRes.data.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderHeatmap = () => {
    const dates = Object.keys(trafficData).sort().slice(-7);
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Find max value for color scaling
    const allValues = dates.flatMap(date => trafficData[date] || Array(24).fill(0));
    const maxValue = Math.max(...allValues, 1);

    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '900px' }}>
          {/* Hour labels */}
          <div style={{ display: 'flex', marginBottom: '8px', paddingLeft: '150px' }}>
            {['0-1', '1-2', '2-3', '3-4', '4-5', '5-6', '6-7', '7-8', '8-9', '9-10', '10-11', '11-12',
              '12-13', '13-14', '14-15', '15-16', '16-17', '17-18', '18-19', '19-20', '20-21', '21-22', '22-23', '23-24'].map((hour, i) => (
              <div key={i} style={{ 
                flex: 1, 
                textAlign: 'center', 
                fontSize: '10px', 
                color: '#6b7280',
                minWidth: '28px'
              }}>
                {hour}
              </div>
            ))}
          </div>

          {/* Heatmap rows */}
          {dates.map((date, dateIndex) => {
            const dateObj = new Date(date);
            const dayName = daysOfWeek[dateObj.getDay()];
            const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            
            return (
              <div key={date} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <div style={{ 
                  width: '150px', 
                  fontSize: '12px', 
                  color: '#9ca3af',
                  textAlign: 'right',
                  paddingRight: '16px'
                }}>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{dayName}</div>
                  <div>{formattedDate}</div>
                </div>
                <div style={{ display: 'flex', gap: '2px', flex: 1 }}>
                  {hours.map(hour => {
                    const value = trafficData[date]?.[hour] || 0;
                    const intensity = value / maxValue;
                    const backgroundColor = value === 0 
                      ? '#f3f4f6' 
                      : `rgba(59, 130, 246, ${0.3 + intensity * 0.7})`;
                    
                    return (
                      <div
                        key={hour}
                        style={{
                          flex: 1,
                          height: '32px',
                          backgroundColor,
                          borderRadius: '2px',
                          minWidth: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          color: value > 0 ? '#fff' : 'transparent',
                          cursor: 'pointer',
                          border: '1px solid #e5e7eb'
                        }}
                        title={`${value} conversations`}
                      >
                        {value > 0 ? value : ''}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading && !conversationStats) {
    return (
      <div className="settings-view">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-view">
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 className="settings-title" style={{ margin: 0 }}>Reports & Analytics</h2>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="form-input"
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '14px',
              width: 'auto'
            }}
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>

        {/* Stats Cards Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {/* Open Conversations Card */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                Open Conversations
              </h3>
              <span style={{
                backgroundColor: '#10b981',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                display: 'inline-block'
              }}></span>
              <span style={{ fontSize: '12px', color: '#10b981' }}>Live</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Open</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {conversationStats?.open || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Unattended</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {conversationStats?.unattended || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Unassigned</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {conversationStats?.unassigned || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Pending</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {conversationStats?.pending || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Agent Status Card */}
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                Agent status
              </h3>
              <span style={{
                backgroundColor: '#10b981',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                display: 'inline-block'
              }}></span>
              <span style={{ fontSize: '12px', color: '#10b981' }}>Live</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Online</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {agentStatus?.online || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Busy</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {agentStatus?.busy || 0}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Offline</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                  {agentStatus?.offline || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation Traffic Heatmap */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                Conversation Traffic
              </h3>
              <span style={{
                backgroundColor: '#10b981',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                display: 'inline-block'
              }}></span>
              <span style={{ fontSize: '12px', color: '#10b981' }}>Live</span>
            </div>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>Last 7 days</span>
          </div>
          {renderHeatmap()}
        </div>

        {/* Conversations by Agents */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              Conversations by agents
            </h3>
            <span style={{
              backgroundColor: '#10b981',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              display: 'inline-block'
            }}></span>
            <span style={{ fontSize: '12px', color: '#10b981' }}>Live</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: '600', 
                    color: '#6b7280',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Agent
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: '600', 
                    color: '#6b7280',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Open
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontSize: '12px', 
                    fontWeight: '600', 
                    color: '#6b7280',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Unattended
                  </th>
                </tr>
              </thead>
              <tbody>
                {agentConversations.map((agent, index) => (
                  <tr key={agent.id}>
                    <td style={{ 
                      padding: '16px', 
                      borderBottom: index < agentConversations.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#fff',
                          position: 'relative'
                        }}>
                          {agent.avatar ? (
                            <img src={agent.avatar} alt={agent.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                          ) : (
                            agent.name.substring(0, 2).toUpperCase()
                          )}
                          {agent.isActive && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              width: '10px',
                              height: '10px',
                              backgroundColor: '#10b981',
                              border: '2px solid #fff',
                              borderRadius: '50%'
                            }} />
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                            {agent.name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {agent.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '16px', 
                      fontSize: '14px', 
                      color: '#111827',
                      borderBottom: index < agentConversations.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}>
                      {agent.open}
                    </td>
                    <td style={{ 
                      padding: '16px', 
                      fontSize: '14px', 
                      color: '#111827',
                      borderBottom: index < agentConversations.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}>
                      {agent.unattended}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Showing 1 to {agentConversations.length} of {agentConversations.length} results
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>10 / page</span>
              <button style={{
                padding: '4px 8px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                color: '#6b7280',
                cursor: 'pointer'
              }}>
                «
              </button>
              <button style={{
                padding: '4px 8px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                color: '#6b7280',
                cursor: 'pointer'
              }}>
                ‹
              </button>
              <button style={{
                padding: '4px 12px',
                backgroundColor: '#3b82f6',
                border: 'none',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: '500'
              }}>
                1
              </button>
              <button style={{
                padding: '4px 8px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                color: '#6b7280',
                cursor: 'pointer'
              }}>
                ›
              </button>
              <button style={{
                padding: '4px 8px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                color: '#6b7280',
                cursor: 'pointer'
              }}>
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsView;

